import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, apiUpload } from '@/lib/api';
import { Button } from '@/components/ui/button';

/** Extract a renderable image URL from a value that might be a bare URL,
 *  a data:image string, or Zoho's `<img src="…">` HTML wrapper. Returns
 *  null when the input doesn't look like any of those. Shared by the
 *  Assets column renderer and the image-field legacy preview. */
export function extractImageUrl(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  const t = raw.trim();
  if (!t) return null;
  if (/^https?:\/\//i.test(t) || /^data:image\//i.test(t)) return t;
  const m = t.match(/src\s*=\s*["']([^"']+)["']/i);
  return m?.[1] ?? null;
}

/** The slice of a CrudPage `FieldConfig` this widget actually reads. Declared
 *  locally (rather than importing FieldConfig from `crud-page`) so that
 *  `crud-page` can keep importing the inline task editors, which in turn
 *  render this widget — importing FieldConfig back would close that cycle. */
export interface UploadFieldSpec {
  name: string;
  label?: string;
  type: 'image' | 'file' | string;
  uploadPrefix?: string;
  uploadAccept?: string;
}

// ===== Upload field — multipart POST to /uploads, stores S3 key =====
//
// Used by FieldType 'file' and 'image'. The form value is the S3 key string.
// Image: renders a thumbnail (signed URL fetched on mount).
// File:  renders a "Download" link (also signed URL fetched on mount).
//   field.uploadPrefix sets the S3 key prefix (e.g. 'asset', 'vehicle-files').
//   field.uploadAccept sets the file picker filter (default 'image/*' for image).
export function UploadField({
  f, value, onChange, legacyImageUrl, presignedUrl,
}: {
  f: UploadFieldSpec;
  value: string | null;
  onChange: (key: string | null) => void;
  /** Read-only fallback image URL to show when no upload key is set yet
   *  (legacy Zoho photo). Ignored for type='file'. */
  legacyImageUrl?: string | null;
  /** Pre-resolved signed URL the server returned alongside the key on the
   *  parent record (e.g. `photoUrl` next to `photoKey`). When present we
   *  use it directly and skip the `/uploads/:key/url` round-trip — saves
   *  a request, and also rescues the preview when that endpoint isn't
   *  reachable (S3 misconfig, transient failure). */
  presignedUrl?: string | null;
}) {
  const isImage = f.type === 'image';
  const accept = f.uploadAccept ?? (isImage ? 'image/*' : undefined);
  const prefix = f.uploadPrefix ?? 'uploads';
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState<string | null>(null);

  // Resolve the current key to a signed URL for preview/download. Skip
  // the fetch entirely when the parent already supplied one.
  const { data: urlData } = useQuery({
    queryKey: ['upload-url', value],
    queryFn: async () => {
      if (!value) return null;
      const r = await api<{ key: string; url: string }>(`/uploads/${encodeURIComponent(value)}/url`);
      return r.data ?? null;
    },
    enabled: !!value && !presignedUrl,
    staleTime: 30 * 60 * 1000, // signed URLs are 1h; cache half that
  });
  // Effective preview URL — prefer the parent-supplied one. Falling back
  // to extractImageUrl(value) covers legacy assets where `photoKey` was
  // accidentally populated with a raw URL or Zoho `<img src="...">` HTML
  // string instead of an S3 key.
  const effectiveUrl = presignedUrl ?? urlData?.url ?? (isImage ? extractImageUrl(value) : null);

  async function pickFile(file: File) {
    setBusy(true); setErr(null);
    try {
      const fd = new FormData();
      // Order matters: non-file form fields must appear before the file part
      // for @fastify/multipart to expose them on `part.fields`.
      fd.append('prefix', prefix);
      fd.append('file', file);
      const r = await apiUpload<{ key: string; url: string }>('/uploads', fd);
      if (!r.success || !r.data?.key) throw new Error(r.error?.message || 'Upload failed');
      onChange(r.data.key);
    } catch (e: any) {
      setErr(e?.message ?? String(e));
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  }

  const filename = value ? value.split('/').pop()!.replace(/^\d+-/, '') : null;

  // Show legacy fallback when:
  //   • no upload key is set yet (`value == null`), AND
  //   • caller supplied a legacy URL (read from a sibling field).
  // The block is read-only — replacing requires the upload below.
  const showLegacy = isImage && !value && !!legacyImageUrl;

  return (
    <div className="space-y-2">
      {value && (
        <div className="flex items-center gap-3 rounded-md border bg-muted/30 p-3">
          {isImage && effectiveUrl && (
            <img
              src={effectiveUrl}
              alt={filename ?? ''}
              className="h-64 w-64 object-cover rounded border shrink-0"
            />
          )}
          {!isImage && (
            <div className="text-3xl">📄</div>
          )}
          <div className="flex-1 min-w-0 self-start">
            <div className="text-sm truncate" title={value}>{filename}</div>
            {effectiveUrl && (
              <a href={effectiveUrl} target="_blank" rel="noreferrer" className="text-xs text-primary hover:underline">
                Download / open
              </a>
            )}
          </div>
          <Button type="button" size="sm" variant="ghost" onClick={() => onChange(null)} disabled={busy} className="self-start">
            Remove
          </Button>
        </div>
      )}
      {showLegacy && (
        <div className="flex items-center gap-3 rounded-md border bg-muted/30 p-3">
          <img
            src={legacyImageUrl!}
            alt="Legacy image"
            className="h-64 w-64 object-cover rounded border shrink-0"
          />
          <div className="flex-1 min-w-0 self-start text-xs text-muted-foreground space-y-1">
            <div className="font-medium text-foreground">Legacy image</div>
            <div>Carried over from Zoho. Upload a new one below to replace it (the new file moves into our storage).</div>
          </div>
        </div>
      )}
      <div className="flex items-center gap-2">
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          className="hidden"
          onChange={(e) => { const f = e.target.files?.[0]; if (f) pickFile(f); }}
        />
        <Button type="button" size="sm" variant="outline" onClick={() => inputRef.current?.click()} disabled={busy}>
          {busy ? 'Uploading…' : value ? `Replace ${isImage ? 'image' : 'file'}` : `Upload ${isImage ? 'image' : 'file'}`}
        </Button>
        {err && <span className="text-xs text-destructive">{err}</span>}
      </div>
    </div>
  );
}
